import { AfterViewInit, ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { ArticleCategoryDTO } from '@scip/api';
import { ArticleCategoryControllerService } from '@scip/api';
import { sortBy } from 'lodash-es';
import { SelectItem, TreeNode } from 'primeng/api';
import { ChangeDetectorRef } from '@angular/core';
import { Observable } from 'rxjs';
import { map, switchMap, tap } from 'rxjs/operators';
import { AvanoLoadingIndicatorService } from '@avano/avano-components';
import { translationKeys } from '../../../core';

@Component({
  selector: 'scip-article-category-tree',
  templateUrl: './article-category-tree.component.html',
  styleUrls: ['./article-category-tree.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ArticleCategoryTreeComponent implements OnInit, AfterViewInit {
  @Input() selectedNode: TreeNode;

  @Output() readonly nodeSelected = new EventEmitter<TreeNode>();

  searchTerm = '';

  searchTariffCode: number;

  mainTree: TreeNode[] = [];

  searchMode = 'code';

  searchOptions: SelectItem[] = [];

  cols: any[];

  blockingIdentifier = 'articleCategoryTree';

  // Translations
  tariffCode = translationKeys.articleCategoryTreeComponent.typeScript.tariffCode;
  textSearch = translationKeys.articleCategoryTreeComponent.typeScript.textSearch;

  translationKeys: typeof translationKeys = translationKeys;

  constructor(
    private ac: ArticleCategoryControllerService,
    private translateService: TranslateService,
    private changeDet: ChangeDetectorRef,
    private avanoLoadingIndicatorService: AvanoLoadingIndicatorService
  ) {}

  ngOnInit(): void {
    this.translateService
      .get([this.tariffCode, this.textSearch])
      .pipe(
        switchMap((values: any) => {
          this.searchOptions = [
            { label: values[this.tariffCode], value: 'code' },
            { label: values[this.textSearch], value: 'text' }
          ];

          this.cols = [
            { field: 'text', header: translationKeys.articleCategoryTreeComponent.html.description, width: 70 },
            { field: 'categorySpecificIdentifier', header: translationKeys.articleCategoryTreeComponent.html.tarifftariccode, width: 15 },
            { field: 'rang', header: translationKeys.articleCategoryTreeComponent.html.range, width: 15 }
          ];

          return this.fetchNodes(null, undefined, undefined);
        })
      )
      .subscribe();
  }

  ngAfterViewInit(): void {
    this.avanoLoadingIndicatorService.block([this.blockingIdentifier]);
  }

  getRange(ac: ArticleCategoryDTO): string {
    if (ac.rangeUp && ac.rangeDown) {
      return ac.rangeUp + '-' + ac.rangeDown;
    }
    return undefined;
  }

  search(): void {
    this.fetchNodes(null, undefined, this.searchTerm).subscribe();
  }

  onExpand(event: any): void {
    const nodeId = event.node.data.id;
    this.fetchNodes(nodeId, event.node, undefined).subscribe();
  }

  nodeSelect(event: any): void {
    if (event.node.data.selectable) {
      this.nodeSelected.emit(event.node);
    } else {
      this.nodeSelected.emit(null);
    }
  }

  nodeUnselect(): void {
    this.nodeSelected.emit(null);
  }

  private convertToTreeNode(data: ArticleCategoryDTO[], parentNode: TreeNode) {
    const tree: TreeNode[] = [];
    data.forEach((item: ArticleCategoryDTO) => {
      const node: TreeNode = {
        data: {
          identifier: item.identifier,
          id: item.id,
          text: item.text,
          categorySpecificIdentifier: item.categorySpecificIdentifier,
          selectable: item.selectable,
          rang: this.getRange(item)
        },
        leaf: item.numberOfChildren === 0 ? true : false
      };
      tree.push(node);
    });
    if (parentNode) {
      parentNode.children = tree;
      this.mainTree = [...this.mainTree];
    } else {
      this.mainTree = tree;
    }
  }

  private convertToSubtree(flatTree: ArticleCategoryDTO[]) {
    const node: TreeNode = {
      data: {
        identifier: flatTree[0].identifier,
        id: flatTree[0].id,
        text: flatTree[0].text,
        categorySpecificIdentifier: flatTree[0].categorySpecificIdentifier,
        selectable: flatTree[0].selectable,
        rang: this.getRange(flatTree[0])
      },
      children: [],
      expanded: true,
      leaf: flatTree[0].numberOfChildren === 0 ? true : false
    };
    if (flatTree.length !== 1) {
      node.children = [];
      flatTree.splice(0, 1);
      node.children.push(this.convertToSubtree(flatTree));
    } else {
      node.expanded = false;
    }
    return node;
  }

  private fetchNodes(nodeIdentifier: any, parentNode: TreeNode, searchTerm: string): Observable<ArticleCategoryDTO[]> {
    this.avanoLoadingIndicatorService.block([this.blockingIdentifier]);
    if (searchTerm === '' || searchTerm === undefined) {
      // The string null can be replaced after SCIP-1689 (Backend improvement task)
      const nodeIdentifierParam = nodeIdentifier ? nodeIdentifier : 'null';
      return this.ac.findArticleCategoriesByParentId(nodeIdentifierParam).pipe(
        tap((data: ArticleCategoryDTO[]) => {
          if (data && data.length > 0) {
            data = sortBy(data, ['id']);
          }
          this.convertToTreeNode(data, parentNode);
          this.avanoLoadingIndicatorService.unblock([this.blockingIdentifier]);
          this.changeDet.markForCheck();
        })
      );
    } else {
      if (this.searchMode === 'text') {
        return this.ac.getResultsOfTopLevelNodesContainingTextSearchTerm(searchTerm).pipe(
          map((data: Set<ArticleCategoryDTO>) => Array.from(data.values())),
          tap((data: ArticleCategoryDTO[]) => {
            if (data && data.length > 0) {
              data = sortBy(data, ['id']);
            }
            this.convertToTreeNode(data, parentNode);
            this.avanoLoadingIndicatorService.unblock([this.blockingIdentifier]);
            this.changeDet.markForCheck();
          })
        );
      } else if (this.searchMode === 'code') {
        return this.ac.getSubTreeContainingTarCode(searchTerm).pipe(
          tap((data: ArticleCategoryDTO[]) => {
            const tree: TreeNode[] = [];
            if (data.length > 0) {
              tree.push(this.convertToSubtree(data));
            }
            this.mainTree = tree;
            this.avanoLoadingIndicatorService.unblock([this.blockingIdentifier]);
            this.changeDet.markForCheck();
          })
        );
      }
    }
  }
}
